import { useAuthService } from '@/services/authService';
import { getErrorMessage } from '@/utils/error-handling';

import {
  useWorkspacesMyInvitationsList,
  useWorkspacesInvitationsAcceptCreate,
  useWorkspacesInvitationsDeclineCreate,
  useWorkspacesCreate,
} from '@/gen-api/api';
import { WorkspaceRequest } from '@/gen-api/models';
import { useToast } from '@/hooks/use-toast';

import { WorkspaceFormValues } from '@/components/auth/CreateWorkspaceForm';
import { useNavigate } from 'react-router-dom';
import { useEffect, useCallback, useState } from 'react';
import { useAuthStore } from '@/store/authStore';

// Custom hook for invitation management
export function useInvitationManagement() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { checkAuthStatus } = useAuthService();
  const [isRefetching, setIsRefetching] = useState(false);

  const {
    data: invitations,
    isLoading: isLoadingInvitations,
    error: invitationsError,
    refetch: refetchInvitations,
  } = useWorkspacesMyInvitationsList({
    query: {
      enabled: !!useAuthStore.getState().userInfo,
    },
  });

  const {
    mutateAsync: acceptInvitation,
    isPending: isAccepting,
    isSuccess: acceptSuccess,
    isError: acceptError,
    error: acceptErrorData,
  } = useWorkspacesInvitationsAcceptCreate();

  const {
    mutate: declineInvitation,
    isPending: isDeclining,
    isSuccess: declineSuccess,
    isError: declineError,
    error: declineErrorData,
  } = useWorkspacesInvitationsDeclineCreate();

  // Handle invitation acceptance success
  useEffect(() => {
    if (acceptSuccess) {
      toast({
        title: 'Success',
        description: 'Invitation accepted! Redirecting to your workspace...',
      });

      // Refresh auth status to update workspaces and currentWorkspace
      checkAuthStatus()
        .then(() => {
          // Redirect to dashboard after auth status is refreshed
          window.location.href = '/dashboard';
        })
        .catch((error) => {
          console.error('Failed to refresh auth status after invitation acceptance:', error);
          // Still redirect even if refresh fails
          window.location.href = '/dashboard';
        });
    }
  }, [acceptSuccess, navigate, toast, checkAuthStatus]);

  // Handle invitation acceptance error
  useEffect(() => {
    if (acceptError && acceptErrorData) {
      console.error('Invitation acceptance failed:', acceptErrorData);
      toast({
        title: 'Invitation Failed',
        description: `Failed to accept invitation: ${getErrorMessage(acceptErrorData)}`,
        variant: 'destructive',
      });
    }
  }, [acceptError, acceptErrorData, toast]);

  // Handle decline invitation success
  useEffect(() => {
    if (declineSuccess) {
      toast({
        title: 'Success',
        description: 'Invitation declined.',
      });
      setIsRefetching(true);
      refetchInvitations().finally(() => {
        setIsRefetching(false);
      });
    }
  }, [declineSuccess, toast, refetchInvitations]);

  // Handle decline invitation error
  useEffect(() => {
    if (declineError && declineErrorData) {
      console.error('Invitation decline failed:', declineErrorData);
      toast({
        title: 'Invitation Failed',
        description: `Failed to decline invitation: ${getErrorMessage(declineErrorData)}`,
        variant: 'destructive',
      });
    }
  }, [declineError, declineErrorData, toast]);

  const handleAcceptInvitation = useCallback(
    async (token: string) => {
      await acceptInvitation({ token });
      navigate('/');
    },
    [acceptInvitation, navigate]
  );

  const handleDeclineInvitation = useCallback(
    (token: string) => {
      declineInvitation({ token });
    },
    [declineInvitation]
  );

  return {
    invitations,
    isLoadingInvitations: isLoadingInvitations || isRefetching,
    hasInvitationsError: !!invitationsError,
    handleAcceptInvitation,
    handleDeclineInvitation,
    isAccepting,
    isDeclining,
  };
}

// Custom hook for workspace creation
export function useWorkspaceCreation() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { checkAuthStatus } = useAuthService();
  const [createdWorkspace, setCreatedWorkspace] = useState<{
    id: string | null;
    name: string;
    task_id?: string;
    status?: string;
  } | null>(null);

  const {
    mutate: createWorkspace,
    isPending: isCreatingWorkspace,
    isSuccess: createSuccess,
    isError: createError,
    error: createErrorData,
    data: createResponseData,
  } = useWorkspacesCreate();

  // Handle workspace creation error
  useEffect(() => {
    if (createError && createErrorData) {
      toast({
        title: 'Workspace Creation Failed',
        description: `Failed to create workspace: ${getErrorMessage(createErrorData)}`,
        variant: 'destructive',
      });
    }
  }, [createError, createErrorData, toast]);

  // Handle workspace creation response (processing status)
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any = createResponseData;
    if (createSuccess && data) {
      if (data.status === 'processing') {
        setCreatedWorkspace({
          id: null,
          name: data.workspace_name,
          task_id: data.task_id,
          status: 'processing',
        });
      }
    }
  }, [createSuccess, createResponseData]);

  const handleCreateWorkspace = useCallback(
    (values: WorkspaceFormValues) => {
      const payload: WorkspaceRequest = {
        name: values.name,
      };
      createWorkspace({ data: payload });
    },
    [createWorkspace]
  );

  const handleWorkspaceReady = useCallback(async () => {
    try {
      await checkAuthStatus();
      setTimeout(() => {
        window.location.href = '/dashboard';
      }, 1000);
    } catch (error) {
      console.error('Error refreshing auth after workspace creation:', error);
      // Still redirect even if auth check fails
      window.location.href = '/dashboard';
    }
  }, [checkAuthStatus, navigate]);

  return {
    handleCreateWorkspace,
    isCreatingWorkspace,
    createdWorkspace,
    handleWorkspaceReady,
  };
}
